import { AnimatePresence, motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { X } from "@phosphor-icons/react";
import { StatusBadge } from "../StatusBadge";
import { PLATFORM_BY_KEY } from "../../platforms";

// Read-only side panel for a single prompt template. Only fields actually present are rendered.
export function TemplateDetailPanel({ template, onClose, catLabel }) {
  const { t } = useTranslation();
  if (!template) return <AnimatePresence />;
  const plat = template.platform ? PLATFORM_BY_KEY[template.platform] : null;
  const Field = ({ label, value }) => value == null || value === "" ? null : (
    <div><div className="promo-overline">{label}</div><div className="mt-1 break-words text-sm font-medium">{value}</div></div>
  );
  return (
    <AnimatePresence>
      {template && (
        <div className="fixed inset-0 z-[60]" data-testid="template-detail-panel">
          <motion.div className="absolute inset-0 bg-background/70 backdrop-blur-sm" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} />
          <motion.aside className="absolute right-0 top-0 flex h-full w-full max-w-md flex-col border-l border-border bg-card"
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }} transition={{ type: "tween", duration: 0.25 }}>
            <div className="flex items-center justify-between border-b border-border px-5 py-4">
              <div className="flex min-w-0 items-center gap-2">
                {plat?.Icon && <plat.Icon size={16} />}
                <span className="truncate text-sm font-medium">{template.name}</span>
              </div>
              <button onClick={onClose} className="rounded-md p-1 text-muted-foreground hover:bg-accent" data-testid="template-panel-close"><X size={18} /></button>
            </div>
            <div className="flex-1 space-y-5 overflow-y-auto p-5">
              <div className="grid grid-cols-2 gap-4">
                <Field label={t("promo.templates.detail.key")} value={<span className="font-mono text-xs">{template.key}</span>} />
                <Field label={t("promo.templates.detail.version")} value={`v${template.versions}`} />
                <Field label={t("promo.templates.col.category")} value={catLabel(template.category)} />
                {plat && <Field label={t("promo.templates.col.platform")} value={plat.label} />}
                <div><div className="promo-overline">{t("promo.templates.col.status")}</div>
                  <div className="mt-1"><StatusBadge status="active" label={t("promo.templates.status.active")} /></div></div>
              </div>

              {template.variables.length > 0 && (
                <div>
                  <div className="promo-overline mb-2">{t("promo.templates.detail.variables")}</div>
                  <div className="flex flex-wrap gap-1.5">
                    {template.variables.map((v) => (
                      <span key={v} className="rounded-full border border-border bg-accent/30 px-2 py-0.5 font-mono text-xs text-muted-foreground">{`{${v}}`}</span>
                    ))}
                  </div>
                </div>
              )}

              {template.template && (
                <div>
                  <div className="promo-overline mb-2">{t("promo.templates.detail.preview")}</div>
                  <pre className="overflow-x-auto whitespace-pre-wrap break-words rounded-lg border border-border bg-accent/20 p-3 font-mono text-[11px] leading-relaxed text-foreground/90" data-testid="template-preview">
{template.template}
                  </pre>
                </div>
              )}

              <div>
                <div className="promo-overline mb-2">{t("promo.templates.detail.metadata")}</div>
                <pre className="overflow-x-auto rounded-lg border border-border bg-accent/20 p-3 font-mono text-[11px] leading-relaxed text-foreground/90" data-testid="template-metadata">
{JSON.stringify({ key: template.key, versions: template.versions, category: template.category, platform: template.platform, variables: template.variables }, null, 2)}
                </pre>
              </div>
            </div>
          </motion.aside>
        </div>
      )}
    </AnimatePresence>
  );
}
